import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import styles from './poster-pages-carousel.module.scss';
import type {PosterPageMeta, PosterPagesCarouselProps} from '@Components/poster-preview-container/components/poster-pages-carousel/poster-pages-carousel.types';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {PosterPagesCarouselItem} from '@Components/poster-preview-container/components/poster-pages-carousel-item/poster-pages-carousel-item';

export function PosterPagesCarousel({...props}: PosterPagesCarouselProps): ReactElement {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [selectedPageIndex, setSelectedPageIndex] = useState<number>(props.preSelectedPageIndex ?? 0);
  const MINIMUM_PAGES_TO_CENTER = 4;

  useEffect(() => {
    if (props.preSelectedPageIndex !== undefined && props.preSelectedPageIndex !== selectedPageIndex) {
      setSelectedPageIndex(props.preSelectedPageIndex);
      scrollItemToCenter(props.preSelectedPageIndex);
    }
  }, [props.preSelectedPageIndex]);

  const getSubtitleForPage = (pageIndex: number): string | undefined => {
    if (!props.showSubtitles) {
      return undefined;
    }
    return props.pageMetas[pageIndex]?.subtitle;
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateArrows = (): void => {
      if (!container) return;
      const {scrollLeft, scrollWidth, clientWidth} = container;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth);
    };

    const observer = new ResizeObserver(() => {
      if (scrollContainerRef.current) {
        updateArrows();
      }
    });

    updateArrows();
    container.addEventListener('scroll', updateArrows);
    observer.observe(container);

    return (): void => {
      container.removeEventListener('scroll', updateArrows);
      observer.disconnect();
    };
  }, [props.pageMetas]);

  const scrollLeft = (): void => {
    scrollContainerRef.current?.scrollBy({left: -300, behavior: 'smooth'});
  };

  const scrollRight = (): void => {
    scrollContainerRef.current?.scrollBy({left: 300, behavior: 'smooth'});
  };

  const getLeftIcon = (): ReactElement<any> | undefined => {
    return showLeftArrow ? (
      <>
        <Icon
          onClick={scrollLeft}
          size={IconSize.SIZE_ICON_16}
          className={`_position-absolute ${styles.iconLeft}`}
          icon="icon-caret-left"
          shape={IconShape.CIRCLE}
          type={IconType.SHADOW}
        />
      </>
    ) : undefined;
  };

  const getRightIcon = (): ReactElement<any> | undefined => {
    return showRightArrow ? (
      <>
        <Icon
          onClick={scrollRight}
          size={IconSize.SIZE_ICON_16}
          className={`_position-absolute ${styles.iconRight}`}
          icon="icon-caret-right"
          shape={IconShape.CIRCLE}
          type={IconType.SHADOW}
        />
      </>
    ) : undefined;
  };

  const handleItemClick = (pageIndex: number, previewUrl: string, pageHashedId?: string): void => {
    setSelectedPageIndex(pageIndex);
    props.onPageThumbnailClick(pageIndex, previewUrl, pageHashedId);
    scrollItemToCenter(pageIndex);
  };

  const scrollItemToCenter = (pageIndex: number): void => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const items = container.children;
    if (pageIndex < 0 || pageIndex >= items.length) return;

    const item = items[pageIndex] as HTMLElement;
    const containerWidth = container.clientWidth;
    const itemLeft = item.offsetLeft;
    const itemWidth = item.offsetWidth;
    const scrollPosition = itemLeft - containerWidth / 2 + itemWidth / 2;

    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    });
  };

  const getPageThumbnailPreview = (item: PosterPageMeta): ReactElement<any> => {
    return (
      <PosterPagesCarouselItem
        key={item.pageIndex}
        pageIndex={item.pageIndex}
        previewThumbnailUrl={item.previewThumbnailUrl}
        previewUrl={item.previewUrl}
        isSelected={selectedPageIndex === item.pageIndex}
        isVideo={item.isVideo}
        isLoading={props.isLoading}
        subtitle={getSubtitleForPage(item.pageIndex)}
        pageHashedId={item.pageHashedId}
        onItemClick={handleItemClick}
      />
    );
  };

  return (
    <div className="_position-relative">
      {getLeftIcon()}
      <div className={`flex-h-row spacing-p-1 ${styles.carousel} ${props.customCarouselClasses ?? ''} ${!props.disableCenterAlignment && props.pageMetas.length < MINIMUM_PAGES_TO_CENTER ? 'flex-content-center' : ''}`} ref={scrollContainerRef}>
        {props.pageMetas.map((item) => getPageThumbnailPreview(item))}
      </div>
      {getRightIcon()}
    </div>
  );
}
